import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiEye, FiEyeOff } from "react-icons/fi";
import './MerchantPayoutApprovals.css';
import useFocusTrap from '../hooks/useFocusTrap';
import usePagination from '../hooks/usePagination';
import Pagination from './Pagination';

const decodeJwtPayload = (token) => {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
};
const isPayoutTokenValid = (token) => {
  if (!token) return false;
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (payload.exp && Date.now() / 1000 > payload.exp) return false;
  return true;
};

const TYPE_COLORS = {
  CASHBACK:   { bg: '#e8f5e9', color: '#2e7d32' },
  REFUND:     { bg: '#e3f2fd', color: '#1565c0' },
  ADJUSTMENT: { bg: '#f3e5f5', color: '#6a1b9a' },
  IMPS:       { bg: '#fff3e0', color: '#e65100' },
  UPI:        { bg: '#e0f7fa', color: '#00695c' },
};
const typeStyle = (type) => {
  const t = (type || '').toUpperCase();
  const s = TYPE_COLORS[t] || { bg: '#f5f5f5', color: '#555' };
  return {
    padding: '2px 10px', borderRadius: '4px', fontSize: '11px',
    fontWeight: '700', letterSpacing: '0.3px',
    background: s.bg, color: s.color, display: 'inline-block'
  };
};

const fmt = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

// ── Toast component ───────────────────────────────────────────────────────────
const Toast = ({ toasts }) => (
  <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 99999, display: 'flex', flexDirection: 'column', gap: '8px' }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        padding: '12px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '260px', maxWidth: '380px',
        background: t.type === 'error' ? '#fdecea' : t.type === 'warning' ? '#fff8e1' : '#e8f5e9',
        color: t.type === 'error' ? '#c62828' : t.type === 'warning' ? '#f57f17' : '#2e7d32',
        borderLeft: `4px solid ${t.type === 'error' ? '#c62828' : t.type === 'warning' ? '#f9a825' : '#2e7d32'}`,
        animation: 'slideIn 0.2s ease',
      }}>
        {t.type === 'error' ? '❌ ' : t.type === 'warning' ? '⚠️ ' : '✅ '}{t.message}
      </div>
    ))}
  </div>
);

// ── Confirm Dialog (replaces window.confirm) ─────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '28px 32px',
      maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
    }}>
      <div style={{ fontSize: '15px', color: '#333', marginBottom: '24px', lineHeight: '1.5' }}>
        {message}
      </div>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
        <button onClick={onCancel}
          style={{ padding: '8px 20px', background: '#fff', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#555' }}>
          Cancel
        </button>
        <button onClick={onConfirm}
          style={{ padding: '8px 20px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
          Confirm
        </button>
      </div>
    </div>
  </div>
);

const MerchantPayoutApprovals = () => {
  const getPayoutTokenKey = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      return `payoutToken_${userData.merchantId || 'unknown'}`;
    } catch { return 'payoutToken_unknown'; }
  };

  const [payoutToken, setPayoutToken] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '', payoutKey: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPayoutKey, setShowPayoutKey] = useState(false);

  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Toast state
  const [toasts, setToasts] = useState([]);
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  // Confirm dialog state
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const askConfirm = (message) => new Promise(resolve => {
    setConfirm({ message, resolve });
  });
  const handleConfirmOk = () => { confirm.resolve(true); setConfirm(null); };
  const handleConfirmCancel = () => { confirm.resolve(false); setConfirm(null); };

  const pagination = usePagination(
    pendingPayouts.filter(p => {
      const q = searchQuery.toLowerCase();
      return !q ||
        (p.referenceId || '').toLowerCase().includes(q) ||
        (p.beneficiaryName || p.impsBeneName || '').toLowerCase().includes(q) ||
        (p.beneficiaryVpa || '').toLowerCase().includes(q);
    }),
    10
  );

  useEffect(() => {
    const storedToken = sessionStorage.getItem(getPayoutTokenKey());
    if (isPayoutTokenValid(storedToken)) setPayoutToken(storedToken);
  }, []);

  useEffect(() => {
    if (payoutToken) fetchPendingPayouts();
    else setLoading(false);
  }, [payoutToken]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      let merchantId = null;
      try { merchantId = JSON.parse(localStorage.getItem('userData') || '{}').merchantId; } catch (_) {}
      const payload = { ...loginForm };
      if (merchantId) payload.merchantId = merchantId;
      const res = await axios.post('/api/auth/payout-login', payload, { withCredentials: true });
      if (res.data.success && res.data.data?.token) {
        const token = res.data.data.token;
        sessionStorage.setItem(getPayoutTokenKey(), token);
        setPayoutToken(token);
        setLoginForm({ username: '', password: '', payoutKey: '' });
      } else {
        setLoginError(res.data.message || 'Login failed');
      }
    } catch (err) {
      setLoginError(err.response?.data?.message || err.response?.data?.error || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const fetchPendingPayouts = async () => {
    try {
      const response = await axios.get('/api/v1/payouts?status=PENDING_APPROVAL&size=500&page=0', {
        headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true
      });
      if (response.data?.content) setPendingPayouts(response.data.content);
      else if (Array.isArray(response.data)) setPendingPayouts(response.data);
      else setPendingPayouts([]);
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
      if (error.response?.status === 401) window.location.href = '/merchant/login';
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (payoutId, comments) => {
    setActionLoading(true);
    try {
      const res = await axios.post(
        `/api/v1/payouts/${payoutId}/approve`,
        { comments },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true }
      );
      if (res.data?.payoutId) {
        setSelectedPayout(null);
        showToast('Payout approved successfully');
        fetchPendingPayouts();
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to approve payout', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (payoutId, comments) => {
    setActionLoading(true);
    try {
      const res = await axios.post(
        `/api/v1/payouts/${payoutId}/reject`,
        { comments },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true }
      );
      if (res.data?.payoutId) {
        setSelectedPayout(null);
        showToast('Payout rejected', 'warning');
        fetchPendingPayouts();
      }
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to reject payout', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select at least one payout to approve.', 'warning');
      return;
    }
    const toApprove = pendingPayouts.filter(p => selectedIds.includes(p.payoutId));
    const confirmed = await askConfirm(
      `Approve ${toApprove.length} selected payout${toApprove.length > 1 ? 's' : ''}? They will be sent to the bank immediately.`
    );
    if (!confirmed) return;

    setBulkApproving(true);
    let ok = 0, fail = 0;
    for (const p of toApprove) {
      try {
        await axios.post(`/api/v1/payouts/${p.payoutId}/approve`, { comments: 'Bulk approved' },
          { headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true });
        ok++;
      } catch { fail++; }
    }
    setBulkApproving(false);
    setSelectedIds([]);
    if (fail === 0) showToast(`${ok} payout${ok > 1 ? 's' : ''} approved successfully`);
    else showToast(`${ok} approved, ${fail} failed`, fail > 0 ? 'error' : 'success');
    fetchPendingPayouts();
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) {
      showToast('Please select at least one payout to reject.', 'warning');
      return;
    }
    const toReject = pendingPayouts.filter(p => selectedIds.includes(p.payoutId));
    const confirmed = await askConfirm(
      `Reject ${toReject.length} selected payout${toReject.length > 1 ? 's' : ''}? This action cannot be undone.`
    );
    if (!confirmed) return;

    setBulkRejecting(true);
    let ok = 0, fail = 0;
    for (const p of toReject) {
      try {
        await axios.post(`/api/v1/payouts/${p.payoutId}/reject`, { comments: 'Bulk rejected' },
          { headers: { Authorization: `Bearer ${sessionStorage.getItem(getPayoutTokenKey())}` }, _skipAuthInterceptor: true });
        ok++;
      } catch { fail++; }
    }
    setBulkRejecting(false);
    setSelectedIds([]);
    if (fail === 0) showToast(`${ok} payout${ok > 1 ? 's' : ''} rejected`, 'warning');
    else showToast(`${ok} rejected, ${fail} failed`, fail > 0 ? 'error' : 'warning');
    fetchPendingPayouts();
  };

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const allOnPageSelected = pagination.pageData.length > 0 &&
    pagination.pageData.every(p => selectedIds.includes(p.payoutId));

  const toggleSelectAll = () => {
    if (allOnPageSelected) setSelectedIds(prev => prev.filter(id => !pagination.pageData.map(p => p.payoutId).includes(id)));
    else setSelectedIds(prev => [...new Set([...prev, ...pagination.pageData.map(p => p.payoutId)])]);
  };

  // ── Login screen ─────────────────────────────────────────────────────────
  if (!payoutToken) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '12px', padding: '40px', width: '100%', maxWidth: '420px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          <h3 style={{ textAlign: 'center', marginTop: 0 }}>💸 Payout Access</h3>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '24px' }}>Enter your payout credentials to access approvals.</p>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Username</label>
              <input type="text" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })}
                placeholder="Payout username" required
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Password</label>
              <div className="password-wrapper">
                <input type={showPassword ? 'text' : 'password'} value={loginForm.password}
                  onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="Password" required
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                <span onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                </span>
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px' }}>Payout Key</label>
              <div className="password-wrapper">
                <input type={showPayoutKey ? 'text' : 'password'} value={loginForm.payoutKey}
                  onChange={e => setLoginForm({ ...loginForm, payoutKey: e.target.value })}
                  placeholder="Payout access key" required
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
                <span onClick={() => setShowPayoutKey(!showPayoutKey)}>
                  {showPayoutKey ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                </span>
              </div>
            </div>
            {loginError && <div style={{ padding: '10px', background: '#fdecea', color: '#c62828', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' }}>❌ {loginError}</div>}
            <button type="submit" disabled={loginLoading}
              style={{ width: '100%', padding: '12px', background: '#007a75', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '15px', cursor: 'pointer', fontWeight: '600' }}>
              {loginLoading ? 'Logging in...' : 'Login to Payout Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) return <div className="approvals-loading" role="status" aria-live="polite">Loading pending approvals...</div>;

  // ── Main table view ───────────────────────────────────────────────────────
  return (
    <div className="payout-approvals">
      <Toast toasts={toasts} />
      {confirm && <ConfirmDialog message={confirm.message} onConfirm={handleConfirmOk} onCancel={handleConfirmCancel} />}

      {/* Header */}
      <div className="approvals-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>⏳</span>
          <h2 style={{ margin: 0 }}>Pending Approvals</h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '14px' }}>🔍</span>
            <input
              type="text"
              placeholder="Search by Ref / Beneficiary"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); pagination.goToPage(1); }}
              style={{ padding: '8px 12px 8px 32px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', width: '220px' }}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', userSelect: 'none' }}>
            <input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} style={{ width: '15px', height: '15px' }} />
            Select All
          </label>

          {pendingPayouts.length > 0 && (
            <button onClick={handleBulkApprove} disabled={bulkApproving || bulkRejecting || selectedIds.length === 0}
              style={{ padding: '8px 18px', background: selectedIds.length === 0 ? '#aaa' : '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
              {bulkApproving ? 'Approving...' : selectedIds.length > 0 ? `✓ Approve Selected (${selectedIds.length})` : '✓ Approve Selected'}
            </button>
          )}

          {pendingPayouts.length > 0 && (
            <button onClick={handleBulkReject} disabled={bulkRejecting || bulkApproving || selectedIds.length === 0}
              style={{ padding: '8px 18px', background: selectedIds.length === 0 ? '#aaa' : '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '13px' }}>
              {bulkRejecting ? 'Rejecting...' : selectedIds.length > 0 ? `✕ Reject Selected (${selectedIds.length})` : '✕ Reject Selected'}
            </button>
          )}

          <div className="approvals-count">{pendingPayouts.length} pending</div>
        </div>
      </div>

      {pendingPayouts.length === 0 ? (
        <div className="approvals-empty-state">
          <div className="empty-icon">✓</div>
          <h3>No Pending Approvals</h3>
          <p>All payouts have been reviewed. Check back later for new approval requests.</p>
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#034659', color: '#fff' }}>
                  <th style={th()}><input type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAll} style={{ width: '15px', height: '15px', cursor: 'pointer' }} /></th>
                  <th style={th()}>Reference</th>
                  <th style={th()}>Beneficiary</th>
                  <th style={th()}>Type</th>
                  <th style={th('right')}>Amount (₹)</th>
                  <th style={th()}>Purpose</th>
                  <th style={th()}>Initiated By</th>
                  <th style={th()}>Created On</th>
                  <th style={th('center')}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.pageData.map((payout, i) => (
                  <tr key={payout.payoutId}
                    style={{ borderBottom: '1px solid #f0f0f0', background: selectedIds.includes(payout.payoutId) ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={td('center')}>
                      <input type="checkbox" checked={selectedIds.includes(payout.payoutId)}
                        onChange={() => toggleSelect(payout.payoutId)}
                        style={{ width: '15px', height: '15px', cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...td(), fontFamily: 'monospace', fontSize: '12px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                      {payout.referenceId}
                    </td>
                    <td style={td()}>
                      <div style={{ fontWeight: '600' }}>{payout.impsBeneName || payout.beneficiaryName || payout.beneficiaryVpa || 'N/A'}</div>
                      {payout.beneficiaryAccount && <div style={{ fontSize: '11px', color: '#888' }}>A/C: ****{payout.beneficiaryAccount.slice(-4)}</div>}
                      {payout.ifscCode && <div style={{ fontSize: '11px', color: '#888' }}>{payout.ifscCode}</div>}
                    </td>
                    <td style={td()}>
                      <span style={typeStyle(payout.payoutType)}>{(payout.payoutType || '—').toUpperCase()}</span>
                    </td>
                    <td style={{ ...td('right'), fontWeight: '700', color: '#1a1a1a' }}>
                      {fmt(payout.amount)}
                    </td>
                    <td style={{ ...td(), color: '#555' }}>{payout.purpose || 'Not specified'}</td>
                    <td style={td()}>{payout.initiatedBy || 'System'}</td>
                    <td style={{ ...td(), whiteSpace: 'nowrap', fontSize: '12px', color: '#555' }}>
                      {new Date(payout.createdAt).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}
                    </td>
                    <td style={td('center')}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button onClick={() => setSelectedPayout({ ...payout, action: 'approve' })}
                          style={actionBtn('#2e7d32')}>✓ Approve</button>
                        <button onClick={() => setSelectedPayout({ ...payout, action: 'reject' })}
                          style={actionBtn('#dc3545', true)}>✕ Reject</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0' }}>
            <Pagination {...pagination} pageSize={10} />
          </div>
        </div>
      )}

      {selectedPayout && (
        <ApprovalActionModal
          payout={selectedPayout}
          action={selectedPayout.action}
          onConfirm={(comments) => {
            if (selectedPayout.action === 'approve') handleApprove(selectedPayout.payoutId, comments);
            else handleReject(selectedPayout.payoutId, comments);
          }}
          onCancel={() => setSelectedPayout(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

const th = (align = 'left') => ({ padding: '12px 14px', textAlign: align, fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap', userSelect: 'none' });
const td = (align = 'left') => ({ padding: '10px 14px', textAlign: align, verticalAlign: 'middle' });
const actionBtn = (color, outline = false) => ({
  padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  borderRadius: '4px', whiteSpace: 'nowrap',
  background: outline ? '#fff' : color,
  color: outline ? color : '#fff',
  border: `1px solid ${color}`,
});

const ApprovalActionModal = ({ payout, action, onConfirm, onCancel, loading }) => {
  const modalRef = useFocusTrap(true);
  const [comments, setComments] = useState('');

  return (
    <div className="modal-overlay" role="presentation" onKeyDown={e => e.key === 'Escape' && onCancel()}>
      <div ref={modalRef} className="modal-content approval-modal"
        role="dialog" aria-modal="true" aria-labelledby="approval-modal-title">
        <div className="modal-header">
          <h3 id="approval-modal-title">
            {action === 'approve' ? '✓ Approve Payout' : '✗ Reject Payout'}
          </h3>
          <button className="modal-close" onClick={onCancel} aria-label="Close">&times;</button>
        </div>
        <div className="modal-body">
          <div className="payout-summary">
            {[
              ['Reference ID', payout.referenceId],
              ['Amount', <span style={{ color: '#28a745', fontWeight: '700', fontSize: '16px' }}>{fmt(payout.amount)}</span>],
              ['Beneficiary', `${payout.impsBeneName || payout.beneficiaryName || payout.beneficiaryVpa || 'N/A'}${payout.beneficiaryAccount ? ` | A/C: ${payout.beneficiaryAccount}` : ''}${payout.ifscCode ? ` | ${payout.ifscCode}` : ''}`],
              ['Type', payout.payoutType],
              ['Purpose', payout.purpose || 'Not specified'],
            ].map(([label, value]) => (
              <div className="summary-row" key={label}>
                <span className="summary-label">{label}:</span>
                <span className="summary-value">{value}</span>
              </div>
            ))}
          </div>
          <form onSubmit={e => { e.preventDefault(); onConfirm(comments); }}>
            <div className="form-group">
              <label>Comments {action === 'reject' ? '(Required)' : '(Optional)'}</label>
              <textarea value={comments} onChange={e => setComments(e.target.value)}
                placeholder={`Add comments for ${action === 'approve' ? 'approval' : 'rejection'}...`}
                className="form-control" rows="3" required={action === 'reject'} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>Cancel</button>
              <button type="submit" className={`btn ${action === 'approve' ? 'btn-success' : 'btn-danger'}`} disabled={loading}>
                {loading ? 'Processing...' : action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MerchantPayoutApprovals;
